import {ElementHelper} from "@buildwithflux/core";
import type {IDocumentData, VertexUid} from "@buildwithflux/models";

import type {SelectionUidsState} from "./selectors/selection/derived";

// Re-export types from canonical location to avoid circular dependency
export type {SelectionUidsState} from "./selectors/selection/derived";
export type {ISelectionUidsState as ISelectionUidsStateFromStore} from "./selectors/selection/derived";

/**
 * Pure function to compute selection UIDs state from document data.
 * This is used for non-reactive access in event handlers and hotkey callbacks.
 *
 * NOTE: This function only checks unbaked nodes (document.pcbLayoutNodes).
 * For hooks that need to check both baked and unbaked nodes (e.g., for silk text inside components),
 * use the useSelectionUidsState hook from modules/document/state/hooks/selection.ts instead.
 *
 * Unlike the canonical computeSelectionUidsState in selectors/selection/derived.ts,
 * this version does NOT check for system default rule sets (systemDefaultRulesSuffix)
 * since non-reactive callers only need user-defined rule sets.
 *
 * @param document The document data
 * @returns The selection UIDs state
 */
export function computeSelectionUidsState(document: IDocumentData | null | undefined): SelectionUidsState {
    const emptyResult: SelectionUidsState = {
        all: [],
        elementUids: [],
        symbolUids: [],
        vertexUids: [],
        routeUids: [],
        netUids: [],
        commentThreadUids: [],
        pcbLayoutNodeUids: [],
        pcbLayoutRuleSetUids: [],
        allCount: 0,
        elementUidsCount: 0,
        symbolUidsCount: 0,
        vertexUidsCount: 0,
        routeUidsCount: 0,
        netUidsCount: 0,
        commentThreadUidsCount: 0,
        pcbLayoutNodeUidsCount: 0,
        pcbLayoutRuleSetUidsCount: 0,
    };

    if (!document) {
        return emptyResult;
    }

    const selectedObjectUids = document.selectedObjectUids || [];
    const elements = document.elements;
    const nets = document.nets;
    const routeVertices = document.routeVertices;
    const routes = document.routes;
    const commentThreads = document.comment_threads;
    const pcbLayoutNodes = document.pcbLayoutNodes;
    const pcbLayoutRuleSets = document.pcbLayoutRuleSets;

    // Build element UIDs (matching Redux selectSelectionUidsState behavior - no branch point filtering)
    const elementUids: string[] = [];
    for (const uid of selectedObjectUids) {
        if (elements?.[uid]) {
            elementUids.push(uid);
        }
    }

    // Build symbol UIDs
    const symbolUids: string[] = [];
    for (const uid of selectedObjectUids) {
        const elementUid = ElementHelper.parseSymbolUid(uid)?.elementUid;
        if (elementUid && elements?.[elementUid]) {
            symbolUids.push(uid);
        }
    }

    // Build vertex UIDs
    const vertexUids: VertexUid[] = [];
    for (const uid of selectedObjectUids) {
        if (routeVertices?.[uid]) {
            vertexUids.push(uid as VertexUid);
        }
    }

    // Build route UIDs
    const routeUids: string[] = [];
    for (const uid of selectedObjectUids) {
        if (routes?.[uid]) {
            routeUids.push(uid);
        }
    }

    // Build net UIDs
    const netUids: string[] = [];
    for (const uid of selectedObjectUids) {
        if (nets?.[uid]) {
            netUids.push(uid);
        }
    }

    // Build comment thread UIDs
    const commentThreadUids: string[] = [];
    for (const uid of selectedObjectUids) {
        if (commentThreads?.[uid]) {
            commentThreadUids.push(uid);
        }
    }

    // Build PCB layout node UIDs
    const pcbLayoutNodeUids: string[] = [];
    for (const uid of selectedObjectUids) {
        if (pcbLayoutNodes?.[uid]) {
            pcbLayoutNodeUids.push(uid);
        }
    }

    // Build PCB layout rule set UIDs (only user-defined, not system defaults)
    const pcbLayoutRuleSetUids: string[] = [];
    for (const uid of selectedObjectUids) {
        if (pcbLayoutRuleSets?.[uid]) {
            pcbLayoutRuleSetUids.push(uid);
        }
    }

    return {
        all: selectedObjectUids,
        elementUids,
        symbolUids,
        vertexUids,
        routeUids,
        netUids,
        commentThreadUids,
        pcbLayoutNodeUids,
        pcbLayoutRuleSetUids,
        allCount: selectedObjectUids.length,
        elementUidsCount: elementUids.length,
        symbolUidsCount: symbolUids.length,
        vertexUidsCount: vertexUids.length,
        routeUidsCount: routeUids.length,
        netUidsCount: netUids.length,
        commentThreadUidsCount: commentThreadUids.length,
        pcbLayoutNodeUidsCount: pcbLayoutNodeUids.length,
        pcbLayoutRuleSetUidsCount: pcbLayoutRuleSetUids.length,
    };
}
